import { type GuidanceSituation, type LifecycleDecision } from "./guidanceModel";

type PersistedIntent = "ignored" | "adjusted";

type LifecycleStorageState = {
    version: 2;
    intent?: PersistedIntent;
    ghostNudgeShown: boolean;
};

export type LifecycleWriter = {
    evaluate: (situation: GuidanceSituation | null) => LifecycleDecision;
    ignore: () => void;
    confirmAdjusted: () => void;
    /** Pure peek: true while the nudge is eligible and has never been shown. Writes nothing. */
    isGhostNudgePending: (eligible: boolean) => boolean;
    /** Marks the one-time nudge as shown. Call only when the UI actually displays it. */
    consumeGhostNudge: () => void;
    reset: () => void;
};

const STORAGE_KEY = "gpuDetection.lifecycle";

export class DialogLifecycle {
    private state: LifecycleStorageState;

    constructor() {
        this.state = loadState();
    }

    evaluate(situation: GuidanceSituation | null): LifecycleDecision {
        if (situation === "A") {
            return { surface: "hard-stop", reason: "Browser hardware acceleration is off" };
        }
        if (situation === null) {
            return { surface: "none", reason: "Device classified as healthy" };
        }
        if (this.state.intent) {
            return { surface: "banner", reason: "Misconfigured after a final dialog intent" };
        }
        return { surface: "wizard", reason: "Misconfigured with no final dialog intent" };
    }

    ignore() {
        this.state.intent = "ignored";
        this.save();
    }

    confirmAdjusted() {
        this.state.intent = "adjusted";
        this.save();
    }

    isGhostNudgePending(eligible: boolean): boolean {
        return eligible && !this.state.ghostNudgeShown;
    }

    consumeGhostNudge() {
        if (this.state.ghostNudgeShown) {
            return;
        }

        this.state.ghostNudgeShown = true;
        this.save();
    }

    reset() {
        this.state = createDefaultState();
        this.save();
    }

    getSnapshot() {
        return this.state;
    }

    private save() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
        } catch {
            // Ignore storage failures and continue with in-memory state.
        }
    }
}

/** Process-wide writer used by the store, session hook, and headless path. */
export const sharedDialogLifecycle = new DialogLifecycle();

function loadState(): LifecycleStorageState {
    const raw = tryReadStorage();
    if (!raw) {
        return createDefaultState();
    }

    try {
        const parsed = JSON.parse(raw) as Partial<LifecycleStorageState>;
        if (parsed.version !== 2) {
            return createDefaultState();
        }

        const intent = parsed.intent === "ignored" || parsed.intent === "adjusted" ? parsed.intent : undefined;

        return {
            version: 2,
            intent,
            ghostNudgeShown: Boolean(parsed.ghostNudgeShown),
        };
    } catch {
        return createDefaultState();
    }
}

function tryReadStorage(): string | null {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function createDefaultState(): LifecycleStorageState {
    return {
        version: 2,
        ghostNudgeShown: false,
    };
}
